import React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

type BottomBarItemProps = {
  item: {
    icon: React.ReactNode;
    label: string;
    route: string;
  };
  notificationCount?: number;
  onClick?: () => void;
  isSelected?: boolean;
};

export function BottomBarItem({
  item,
  notificationCount = 0,
  onClick,
  isSelected,
}: BottomBarItemProps) {
  const router = useRouterState();
  const isActive = router.location.pathname.startsWith(item.route);
  const isHighlighted = isActive || isSelected;
  return (
    <Link
      to={item.route}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex h-full min-h-12 min-w-0 flex-1 basis-0 flex-col items-center justify-center gap-1 rounded-xl border py-1 text-center transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        isActive
          ? "border-primary/15 bg-primary/5 text-primary"
          : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-900 active:bg-primary/5 active:text-primary",
      )}
    >
      <div className="relative shrink-0">
        {React.isValidElement(item.icon)
          ? React.cloneElement(
              item.icon as React.ReactElement<{
                className?: string;
                "aria-hidden"?: boolean;
              }>,
              {
                className: cn("size-5", isHighlighted && "stroke-[2.5px]"),
                "aria-hidden": true,
              },
            )
          : item.icon}
        {notificationCount > 0 && (
          <div className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white ring-2 ring-white">
            {notificationCount}
          </div>
        )}
      </div>
      <span
        className={cn(
          "w-full min-w-0 whitespace-normal break-words text-[11px] leading-3 tracking-tight min-[360px]:tracking-normal sm:text-xs",
          isHighlighted ? "font-semibold" : "font-medium",
        )}
      >
        {item.label}
      </span>
    </Link>
  );
}

export default BottomBarItem;
